import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ShoppingBag } from "lucide-react";
import Logo from "./Logo";
import { SITE } from "../data/content";
import { useCart } from "@/context/CartContext";

const NAV = [
  { to: "/about", label: "About" },
  { to: "/what-we-do", label: "What We Do" },
  { to: "/endorsements", label: "Endorsements" },
  { to: "/get-involved", label: "Get Involved" },
  { to: "/events", label: "Events" },
  { to: "/news", label: "News" },
  { to: "/store", label: "Store" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { totalQuantity, openDrawer } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 bg-white transition-shadow ${scrolled ? "shadow-md" : "shadow-sm"}`}
    >
      <div className="container-x flex items-center justify-between py-3 lg:py-4">
        <Link to="/" aria-label="Veterans for Indiana — home" data-testid="logo-home-link" className="flex items-center">
          <Logo className="h-12 w-auto sm:h-14" />
        </Link>

        <nav aria-label="Primary" className="hidden lg:flex items-center gap-7">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <CartButton totalQuantity={totalQuantity} onClick={openDrawer} />
          <a
            href={SITE.donateUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary"
            data-testid="donate-button-header"
          >
            Donate
          </a>
        </div>

        <div className="lg:hidden flex items-center gap-1">
          <CartButton totalQuantity={totalQuantity} onClick={openDrawer} mobile />
          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-nav"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center justify-center rounded-md p-2 text-navy-deep hover:bg-gray-soft"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      <div
        id="mobile-nav"
        className={`lg:hidden overflow-hidden border-t border-gray-200 transition-[max-height,opacity] duration-300 ${
          open ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <nav aria-label="Mobile" className="container-x flex flex-col py-4 gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `py-3 px-2 font-display tracking-widest text-sm uppercase border-b border-gray-100 ${
                  isActive ? "text-green-brand" : "text-navy"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <a
            href={SITE.donateUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary mt-3 self-start"
            data-testid="donate-button-mobile"
          >
            Donate
          </a>
        </nav>
      </div>
    </header>
  );
}

function CartButton({ totalQuantity, onClick, mobile = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Open cart${totalQuantity > 0 ? `, ${totalQuantity} items` : ""}`}
      className={`relative inline-flex items-center justify-center rounded-md text-navy hover:bg-gray-soft transition-colors ${
        mobile ? "p-2" : "h-10 w-10"
      }`}
      data-testid={mobile ? "cart-button-mobile" : "cart-button-header"}
    >
      <ShoppingBag size={22} />
      {totalQuantity > 0 && (
        <span
          className="absolute -top-1 -right-1 bg-green-brand text-white text-[10px] font-semibold min-w-[18px] h-[18px] px-1 rounded-full inline-flex items-center justify-center"
          data-testid="cart-count-badge"
        >
          {totalQuantity}
        </span>
      )}
    </button>
  );
}
